import React from 'react';
import config from '@plone/volto/registry';
import { ImArrowDownRight2 } from 'react-icons/im';

import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';

import './less/TestimonialCard.less';

const MOBILE_MAX_WIDTH_QUERY = '(max-width: 768px)';
const QUOTE_CLAMP_LINES = 4;

function useIsMobileMaxWidth(query = MOBILE_MAX_WIDTH_QUERY) {
  const [matches, setMatches] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

const TestimonialCard = ({ item }) => {
  const PreviewImage = config.getComponent({ name: 'PreviewImage' }).component;
  const [expanded, setExpanded] = React.useState(false);
  const [quoteTruncated, setQuoteTruncated] = React.useState(false);
  const quoteRef = React.useRef(null);
  const isMobile = useIsMobileMaxWidth();

  const quoteId = React.useMemo(
    () =>
      `testimonial-quote-${item.UID ?? (String(item['@id'] ?? '').replace(/[^a-zA-Z0-9_-]/g, '-') || 'item')}`,
    [item.UID, item['@id']],
  );

  const clampQuote = isMobile && !expanded;

  React.useLayoutEffect(() => {
    const el = quoteRef.current;
    if (!el || !isMobile) {
      setQuoteTruncated(false);
      return;
    }

    const measure = () => {
      if (!clampQuote) {
        return;
      }
      setQuoteTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    measure();

    window.addEventListener('resize', measure);
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;
    if (ro) {
      ro.observe(el);
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (ro) {
        ro.disconnect();
      }
    };
  }, [isMobile, clampQuote, item.testimonial_quoted_text]);

  React.useEffect(() => {
    setExpanded(false);
  }, [item.UID, item['@id']]);

  React.useEffect(() => {
    if (!isMobile) {
      setExpanded(false);
    }
  }, [isMobile]);

  const showReadMoreControl = isMobile && (quoteTruncated || expanded);

  return (
    <Card className="testimonial-card">
      <Card.Body className="d-flex flex-column justify-content-between">
        <Row>
          <div
            className={`w-100 d-flex justify-content-center align-items-center ${isMobile ? 'mb-3' : 'mb-4'}`}
          >
            {item.testimonial_image && (
              <PreviewImage
                item={item.testimonial_image}
                alt={item.title}
                image_field="image"
                imageField="image"
                className="ui image testimonial-img rounded-circle object-fit-cover"
                responsive={true}
                loading="lazy"
              />
            )}
          </div>
          <Card.Text
            ref={quoteRef}
            id={quoteId}
            className={`fs-s ${isMobile ? 'mb-1' : 'mb-4'}${clampQuote ? ' text-truncate-x' : ''}`}
            style={clampQuote ? { '--text-lines': QUOTE_CLAMP_LINES } : undefined}
          >
            <span aria-hidden="true">&ldquo;</span>
            {item.testimonial_quoted_text}
            <span aria-hidden="true">&rdquo;</span>
          </Card.Text>
          {showReadMoreControl ? (
            <button
              type="button"
              className="btn btn-link testimonial-card__read-more d-inline-flex align-items-center align-self-start p-0 border-0 mb-3"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={quoteId}
              aria-label={
                expanded
                  ? 'Show less of the testimonial quote'
                  : 'Show full testimonial quote'
              }
            >
              <ImArrowDownRight2
                className={`testimonial-card__read-more-icon me-2${expanded ? ' testimonial-card__read-more-icon--expanded' : ''}`}
                aria-hidden="true"
              />
              {expanded ? 'Read less' : 'Read more'}
            </button>
          ) : null}
        </Row>
        <Row className="d-flex flex-column  align-items-end">
          <Card.Text
            className={`fw-bold mb-1 ${isMobile ? 'fs-xl' : 'fs-xxl'}`}
          >
            {item.title}
          </Card.Text>
          <Card.Text className="mb-1 fst-italic">
            {item.testimonial_rol}
          </Card.Text>
          <Card.Text>{item.testimonial_university_name}</Card.Text>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default TestimonialCard;
